/**
 * A LIVE STREAM'S PLANNED TIMES (1.2.0): when it starts and when it ends, each a date and time on
 * the author's own clock - the field names the zone - kept together by "Save times"
 * (`setStreamTimes`). The website shows the stream as upcoming before the start, live between the
 * two and past after the end.
 *
 * The end must be after the start and at most 24 hours after it (the owner, 28 October 2026): said
 * here before anything is sent, and the platform's own refusal is said as it comes. Whether the start is still ahead is the checklist's to say
 * (./video-checklist.ts `streamTimesRow`), against the platform's instant - the app reads no clock
 * (C11). That instant's day, on the author's clock, is also the first day the fields offer, as
 * Setup's publication date does.
 *
 * Saved by a button rather than as typed: a picker changes its value once for the day, once for the
 * hour and once for the minutes, and each of those is a whole time the platform would keep.
 */
import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { DateTimeField, instantToWall, isKnownTimeZone } from '@moonx/ui';
import { useEditorApi } from '../../features/cabinet/api/editor-api';
import type { StreamTimes, WorkingStream } from '../../features/cabinet/api/editor-api';
import { describeProblem } from '../../features/cabinet/api/problem';
import { useAction } from '../../features/cabinet/state';
import { useTranslation } from '../../i18n';
import classes from './video.module.css';

/** The longest a live stream may be planned for; the platform refuses a longer one. */
const STREAM_LONGEST_MS = 24 * 60 * 60_000;

/** What is wrong with the times on screen, found here before anything is sent. */
export type StreamTimesProblem = 'endBeforeStart' | 'tooLong';

function problemWith(startsAt: string, endsAt: string): StreamTimesProblem | null {
  const length = Date.parse(endsAt) - Date.parse(startsAt);
  return length <= 0 ? 'endBeforeStart' : length > STREAM_LONGEST_MS ? 'tooLong' : null;
}

/** Two instants the same moment however each is spelled (`…:00Z`, `….000Z`), or both absent. */
function sameInstant(a: string | null, b: string | null): boolean {
  return a === null || b === null ? a === b : Date.parse(a) === Date.parse(b);
}

export interface StreamTimesState {
  /** The times the platform holds: what the checklist and Send go by. */
  readonly saved: WorkingStream;
  /** The times on screen. */
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly setStartsAt: (instant: string | null) => void;
  readonly setEndsAt: (instant: string | null) => void;
  /** What is on screen is not what the platform holds: Send waits, and leaving asks first. */
  readonly unsaved: boolean;
  /** Both set: "Save times" may be pressed, and checks the end against the start. */
  readonly saveable: boolean;
  readonly save: () => void;
  readonly saving: boolean;
  /** The end is not after the start, or more than 24 hours after it; nothing was sent. */
  readonly problem: StreamTimesProblem | null;
  readonly error: unknown;
  /** The copy the editor took holds these times (after Send): the screen shows them. */
  readonly take: (stream: WorkingStream | null | undefined) => void;
}

export function useStreamTimes(articleId: string, initial: WorkingStream): StreamTimesState {
  const api = useEditorApi();
  const [saved, setSaved] = useState<WorkingStream>(initial);
  const [startsAt, setStarts] = useState(initial.startsAt);
  const [endsAt, setEnds] = useState(initial.endsAt);
  const [problem, setProblem] = useState<StreamTimesProblem | null>(null);
  const keep = useAction((times: StreamTimes) => api.setStreamTimes(articleId, times));

  const unsaved = !sameInstant(startsAt, saved.startsAt) || !sameInstant(endsAt, saved.endsAt);
  const saveable = startsAt !== null && endsAt !== null;

  const { run, clearError } = keep;
  const save = useCallback(() => {
    if (startsAt === null || endsAt === null) {
      return;
    }
    const found = problemWith(startsAt, endsAt);
    setProblem(found);
    if (found !== null) {
      return;
    }
    void (async () => {
      const kept = await run({ startsAt, endsAt });
      if (kept !== undefined) {
        setSaved(kept);
      }
    })();
  }, [startsAt, endsAt, run]);

  // A change of either field clears what was said about the times before it.
  const setStartsAt = useCallback(
    (instant: string | null) => {
      setStarts(instant);
      setProblem(null);
      clearError();
    },
    [clearError],
  );
  const setEndsAt = useCallback(
    (instant: string | null) => {
      setEnds(instant);
      setProblem(null);
      clearError();
    },
    [clearError],
  );

  const take = useCallback((stream: WorkingStream | null | undefined) => {
    if (stream === null || stream === undefined) {
      return;
    }
    setSaved(stream);
    setStarts(stream.startsAt);
    setEnds(stream.endsAt);
  }, []);

  return useMemo(
    () => ({
      saved,
      startsAt,
      endsAt,
      setStartsAt,
      setEndsAt,
      unsaved,
      saveable,
      save,
      saving: keep.isRunning,
      problem,
      error: keep.error,
      take,
    }),
    [
      saved,
      startsAt,
      endsAt,
      setStartsAt,
      setEndsAt,
      unsaved,
      saveable,
      save,
      keep.isRunning,
      problem,
      keep.error,
      take,
    ],
  );
}

export interface StreamTimesSectionProps {
  readonly state: StreamTimesState;
  /** The platform's own instant (`computedAt`), once read: its day is the first offered. */
  readonly platformNow: string | undefined;
}

export function StreamTimesSection({ state, platformNow }: StreamTimesSectionProps) {
  const { t } = useTranslation('video');
  // Not a clock read: the zone the browser is set to, the author's own clock.
  const named = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zone = isKnownTimeZone(named) ? named : 'UTC';
  const firstDay =
    platformNow === undefined ? undefined : instantToWall(platformNow, zone)?.slice(0, 10);
  const said =
    state.problem !== null
      ? t(`stream.${state.problem}`)
      : state.error === undefined
        ? null
        : `${t('stream.notSaved')} ${describeProblem(state.error, 'video')}`;

  return (
    <section className={classes.section} aria-labelledby="stream-times-title">
      <Stack gap={2}>
        <Title id="stream-times-title" order={2} size="h5">
          {t('stream.timesTitle')}
        </Title>
        <Text className={classes.hint}>{t('stream.timesIntro')}</Text>
      </Stack>
      <DateTimeField
        label={t('stream.starts')}
        placeholder={t('stream.pick')}
        value={state.startsAt}
        onChange={state.setStartsAt}
        timeZone={zone}
        minDay={firstDay}
        openingDay={firstDay}
        defaultTime="18:00"
      />
      <DateTimeField
        label={t('stream.ends')}
        placeholder={t('stream.pick')}
        value={state.endsAt}
        onChange={state.setEndsAt}
        timeZone={zone}
        minDay={firstDay}
        openingDay={firstDay}
        defaultTime="19:00"
      />
      <Group gap="sm" align="center">
        <Button
          size="compact-sm"
          disabled={!state.saveable || !state.unsaved}
          loading={state.saving}
          onClick={state.save}
        >
          {t('stream.save')}
        </Button>
        {/* Always mounted, so the line is announced when it changes. */}
        <Text size="xs" c="dimmed" role="status">
          {state.unsaved ? t('stream.unsaved') : state.saveable ? t('stream.saved') : ''}
        </Text>
      </Group>
      {said === null ? null : (
        <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
          {said}
        </Text>
      )}
    </section>
  );
}
